export namespace Paths {
  export type Vec = [x: number, y: number]

  export type Command =
    | {
        type: "M"
        args: Vec
      }
    | {
        type: "L"
        args: Vec
      }
    | {
        type: "Z"
      }
    | {
        type: "A"
        args: [rx: number, ry: number, angle: number, largeArcFlag: number, sweepFlag: number, x: number, y: number]
      }

  // Returns the exact line length, without approximations
  export function getLength(path: Command[]) {
    let length = 0
    let prev: Vec | null = null
    let subpathStart: Vec | null = null

    for (let i = 0; i <= path.length; i++) {
      const cmd = path[i % path.length]
      if (cmd.type === "M") {
        prev = cmd.args
        subpathStart = cmd.args
      } else if (cmd.type === "L") {
        if (prev) {
          const dx = cmd.args[0] - prev[0]
          const dy = cmd.args[1] - prev[1]
          length += Math.sqrt(dx * dx + dy * dy)
          prev = cmd.args
        }
      } else if (cmd.type === "Z") {
        if (prev && subpathStart) {
          const dx = subpathStart[0] - prev[0]
          const dy = subpathStart[1] - prev[1]
          length += Math.hypot(dx, dy)
          prev = subpathStart
        }
      } else if (cmd.type === "A") {
        if (prev) {
          const [rx, ry, angle, largeArcFlag, sweepFlag, x, y] = cmd.args
          const r = rx // assuming rx === ry and it's a circle

          const arcLength = r * (Math.PI / 2) // 90° arc
          length += arcLength

          prev = [x, y]
        }
      }
    }

    return length
  }

  export function stringify(path: Command[]) {
    return path
      .map((item) => {
        switch (item.type) {
          case "M":
            return `M${item.args.join(",")}`
          case "L":
            return `L${item.args.join(",")}`
          case "Z":
            return `Z`
          case "A":
            return `A ${item.args.join(" ")}`
        }
      })
      .join("\n")
  }

  export function toPolygon(path: Command[], segmentAmount = 8, scale = 1): Vec[] {
    const points: Vec[] = []
    let start: Vec | null = null
    let prev: Vec | null = null

    for (const cmd of path) {
      if (cmd.type === "M") {
        start = [cmd.args[0] * scale, cmd.args[1] * scale]
        prev = start
        points.push(start)
      } else if (cmd.type === "L") {
        if (prev) {
          const curr: Vec = [cmd.args[0] * scale, cmd.args[1] * scale]
          points.push(curr)
          prev = curr
        }
      } else if (cmd.type === "A") {
        if (prev) {
          const radius = cmd.args[0] * scale
          const largeArcFlag = cmd.args[3]
          const sweepFlag = cmd.args[4]
          const curr: Vec = [cmd.args[5] * scale, cmd.args[6] * scale]

          // Calculate the center of the arc
          const dx = (prev[0] - curr[0]) / 2
          const dy = (prev[1] - curr[1]) / 2
          const dist = Math.sqrt(dx * dx + dy * dy)
          const r = radius

          if (dist > r) {
            // If the distance is greater than the radius, treat it as a line
            points.push(curr)
            prev = curr
            continue
          }

          const h = Math.sqrt(r * r - dist * dist)
          const cx = (prev[0] + curr[0]) / 2 + ((h * dy) / dist) * (sweepFlag === largeArcFlag ? -1 : 1)
          const cy = (prev[1] + curr[1]) / 2 - ((h * dx) / dist) * (sweepFlag === largeArcFlag ? -1 : 1)

          // Calculate start and end angles
          const startAngle = Math.atan2(prev[1] - cy, prev[0] - cx)
          const endAngle = Math.atan2(curr[1] - cy, curr[0] - cx)

          // Determine the direction of the arc
          let angleDiff = endAngle - startAngle
          if (sweepFlag === 0 && angleDiff > 0) {
            angleDiff -= 2 * Math.PI
          } else if (sweepFlag === 1 && angleDiff < 0) {
            angleDiff += 2 * Math.PI
          }

          // Generate points along the arc
          const segments = 8
          for (let i = 1; i <= segments; i++) {
            const t = i / segments
            const theta = startAngle + t * angleDiff
            const x = cx + r * Math.cos(theta)
            const y = cy + r * Math.sin(theta)
            points.push([x, y])
          }

          prev = curr
        }
      }
    }

    return points
  }

  export function sdfPolygon(p: Vec, poly: Vec[]): number {
    if (poly.length < 5) return Infinity
    const px = p[0],
      py = p[1]
    const n = poly.length
    let inside = false
    let minDistSq = Infinity

    // start with last vertex to avoid modulo per edge
    let ax = poly[n - 1][0],
      ay = poly[n - 1][1]

    for (let i = 0; i < n; i++) {
      const bx = poly[i][0],
        by = poly[i][1]

      // edge vector
      const ex = bx - ax,
        ey = by - ay
      const e2 = ex * ex + ey * ey
      if (e2 !== 0) {
        // vector from a to p
        const wx = px - ax,
          wy = py - ay

        // projection factor (manual clamp is faster than nested Math.min/Math.max)
        let t = (wx * ex + wy * ey) / e2
        if (t < 0) t = 0
        else if (t > 1) t = 1

        // closest point distance^2 (no temp array)
        const cx = ax + t * ex,
          cy = ay + t * ey
        const dx = px - cx,
          dy = py - cy
        const d2 = dx * dx + dy * dy
        if (d2 < minDistSq) minDistSq = d2
      }

      // point-in-polygon (ray cast), computed with one division only when crossing
      const ayAbove = ay > py
      const byAbove = by > py
      if (ayAbove !== byAbove) {
        // Only compute intersection x when the ray crosses the edge
        const xInt = (ex * (py - ay)) / (by - ay) + ax
        if (px < xInt) inside = !inside
      }

      ax = bx
      ay = by // advance edge
    }

    const dist = Math.sqrt(minDistSq)
    return inside ? -dist : dist
  }

  /** Similar to sdfPolygon, but works with Path commands — taking into account corner radii. It assumes the rx and ry are both equal */
  export function sdfPath(p: Vec, path: Command[]): number {
    let inside = false
    let minDistSq = Infinity
    let start: Vec | null = null
    let prev: Vec | null = null
    let prevRadius = 0

    for (let i = 0; i < path.length; i++) {
      const cmd = path[i]
      if (cmd.type === "M") {
        start = [cmd.args[0], cmd.args[1]]
        prev = start
        prevRadius = 0
      } else if (cmd.type === "L") {
        if (!prev) continue
        const curr: Vec = [cmd.args[0], cmd.args[1]]
        const nextCmd = path[(i + 1) % path.length]
        const next = nextCmd.type === "L" ? ([nextCmd.args[0], nextCmd.args[1]] as Vec)! : start!
        const nextRadius = nextCmd.type === "A" ? nextCmd.args[0] : 0

        // Edge vector and relative vector
        const e: Vec = [curr[0] - prev[0], curr[1] - prev[1]]
        const w: Vec = [p[0] - prev[0], p[1] - prev[1]]

        // Projection factor onto edge, clamped [0,1]
        const t = Math.max(0, Math.min(1, (w[0] * e[0] + w[1] * e[1]) / (e[0] * e[0] + e[1] * e[1])))

        // Closest point on edge
        const closest: Vec = [prev[0] + t * e[0], prev[1] + t * e[1]]
        const dx = p[0] - closest[0]
        const dy = p[1] - closest[1]
        const distSq = dx * dx + dy * dy

        if (distSq < minDistSq) minDistSq = distSq

        // Winding check (cross product sign flip)
        const cond =
          prev[1] > p[1] !== curr[1] > p[1] &&
          p[0] < ((curr[0] - prev[0]) * (p[1] - prev[1])) / (curr[1] - prev[1]) + prev[0]
        if (cond) inside = !inside

        // Corner radius check (if either prev or next has a radius)
        if (prevRadius > 0 || nextRadius > 0) {
          const corner = curr
          const toPrev = Math.atan2(prev[1] - corner[1], prev[0] - corner[0])
          const toNext = Math.atan2(next[1] - corner[1], next[0] - corner[0])
          const angleDiff = ((toNext - toPrev + Math.PI * 2) % (Math.PI * 2)) - Math.PI
          const angleBisect = toPrev + angleDiff / 2

          const radius = Math.min(prevRadius, nextRadius)
          const center: Vec = [corner[0] + Math.cos(angleBisect) * radius, corner[1] + Math.sin(angleBisect) * radius]

          const cdx = p[0] - center[0]
          const cdy = p[1] - center[1]
          const distToCenterSq = cdx * cdx + cdy * cdy
          const distToEdgeSq = (Math.sqrt(distToCenterSq) - radius) ** 2

          if (distToEdgeSq < minDistSq) minDistSq = distToEdgeSq
        }

        prev = curr
        prevRadius = nextRadius
      } else if (cmd.type === "A") {
        // Handled as part of the previous or next line segment
        continue
      } else if (cmd.type === "Z") {
        if (prev && start) {
          const curr: Vec = start
          const nextCmd = path[(i + 1) % path.length]
          const next = nextCmd.type === "L" ? ([nextCmd.args[0], nextCmd.args[1]] as Vec) : start
          const nextRadius = nextCmd.type === "A" ? nextCmd.args[0] : 0

          // Edge vector and relative vector
          const e: Vec = [curr[0] - prev[0], curr[1] - prev[1]]
          const w: Vec = [p[0] - prev[0], p[1] - prev[1]]

          // Projection factor onto edge, clamped [0,1]
          const t = Math.max(0, Math.min(1, (w[0] * e[0] + w[1] * e[1]) / (e[0] * e[0] + e[1] * e[1])))

          // Closest point on edge
          const closest: Vec = [prev[0] + t * e[0], prev[1] + t * e[1]]
          const dx = p[0] - closest[0]
          const dy = p[1] - closest[1]
          const distSq = dx * dx + dy * dy

          if (distSq < minDistSq) minDistSq = distSq

          // Winding check (cross product sign flip)
          const cond =
            prev[1] > p[1] !== curr[1] > p[1] &&
            p[0] < ((curr[0] - prev[0]) * (p[1] - prev[1])) / (curr[1] - prev[1]) + prev[0]
          if (cond) inside = !inside

          // Corner radius check (if either prev or next has a radius)
          if (prevRadius > 0 || nextRadius > 0) {
            const corner = curr
            const toPrev = Math.atan2(prev[1] - corner[1], prev[0] - corner[0])
            const toNext = Math.atan2(next[1] - corner[1], next[0] - corner[0])
            const angleDiff = ((toNext - toPrev + Math.PI * 2) % (Math.PI * 2)) - Math.PI
            const angleBisect = toPrev + angleDiff / 2

            const radius = Math.min(prevRadius, nextRadius)
            const center: Vec = [corner[0] + Math.cos(angleBisect) * radius, corner[1] + Math.sin(angleBisect) * radius]

            const cdx = p[0] - center[0]
            const cdy = p[1] - center[1]
            const distToCenterSq = cdx * cdx + cdy * cdy
            const distToEdgeSq = (Math.sqrt(distToCenterSq) - radius) ** 2

            if (distToEdgeSq < minDistSq) minDistSq = distToEdgeSq
          }

          prev = curr
          prevRadius = nextRadius
        }
      }
    }

    const dist = Math.sqrt(minDistSq)
    return inside ? -dist : dist
  }

  export function catmullRomToBezierPath(
    pts: Vec[],
    { closed = true, tension = 0, scale = 1 }: { closed?: boolean; tension?: number; scale?: number } = {},
  ): string {
    const n = pts.length

    // Cardinal tension factor (0 = Catmull–Rom)
    const k = (1 - tension) / 6

    // Helper to access points with endpoint handling
    const at = (i: number): Vec => {
      if (closed) return pts[(i + n) % n]
      // mirror endpoints for open curve
      if (i < 0) return [2 * pts[0][0] - pts[1][0], 2 * pts[0][1] - pts[1][1]]
      if (i >= n) return [2 * pts[n - 1][0] - pts[n - 2][0], 2 * pts[n - 1][1] - pts[n - 2][1]]
      return pts[i]
    }

    let d = `M${pts[0][0] * scale},${pts[0][1] * scale}`
    const segCount = closed ? n : n - 1

    for (let i = 0; i < segCount; i++) {
      const p0 = at(i - 1)
      const p1 = at(i)
      const p2 = at(i + 1)
      const p3 = at(i + 2)

      const c1: Vec = [p1[0] + k * (p2[0] - p0[0]), p1[1] + k * (p2[1] - p0[1])]
      const c2: Vec = [p2[0] - k * (p3[0] - p1[0]), p2[1] - k * (p3[1] - p1[1])]

      d += ` C${c1[0] * scale},${c1[1] * scale} ${c2[0] * scale},${c2[1] * scale} ${p2[0] * scale},${p2[1] * scale}`
    }

    if (closed) d += " Z"
    return d
  }

  export function catmullRomToBezierCanvasPath(pts: Vec[], { tension = 0 }: { tension?: number } = {}): Path2D | null {
    const n = pts.length
    const path = new Path2D()

    if (n < 5) return null

    const k = (1 - tension) / 6

    const at = (i: number): Vec => {
      return pts[(i + n) % n]
    }

    path.moveTo(pts[0][0], pts[0][1])
    const segCount = n

    let last = [-1, -1]
    for (let i = 0; i < segCount; i++) {
      const p0 = at(i - 1)
      const p1 = at(i)
      const p2 = at(i + 1)
      const p3 = at(i + 2)

      const c1x = p1[0] + k * (p2[0] - p0[0])
      const c1y = p1[1] + k * (p2[1] - p0[1])
      const c2x = p2[0] - k * (p3[0] - p1[0])
      const c2y = p2[1] - k * (p3[1] - p1[1])

      // path.lineTo(p2[0], p2[1])
      path.bezierCurveTo(c1x, c1y, c2x, c2y, p2[0], p2[1])
      // path.quadraticCurveTo(c1x, c1y, (c1x + c2x) / 2, (c1y + c2y) / 2)
      // path.quadraticCurveTo(c2x, c2y, p2[0], p2[1])
    }

    path.closePath()
    return path
  }

  export const catmullrom = (data: Vec[], alpha: number) => {
    if (alpha === 0 || alpha === undefined) {
      return false
    }

    const result = new Path2D()

    let lastStartPoint = data[0]

    const length = data.length
    const alpha2 = alpha * 2

    const n = data.length
    const at = (i: number): Vec => {
      return data[(i + n) % n]
    }

    for (let i = 0; i <= length; i++) {
      // let p0 = i === 0 ? data[0] : data[i - 1]
      // let p1 = data[i]
      // let p2 = data[i + 1]
      // let p3 = i + 2 < length ? data[i + 2] : p2
      let p0 = at(i - 1)
      let p1 = at(i)
      let p2 = at(i + 1)
      let p3 = at(i + 2)

      // C-R knots are defined as
      // t_{i+1} = | P_{i+1} - P_{i} | ^ alpha + t_{i} where alpha ∈ [0,1], t_{0} = 0
      // alpha -> 𝛂

      // now lest use parameter values as
      // d_{i+1} = | P_{i+1} - P_{i} |  where d_{0} = 0

      const d1 = Math.sqrt((p0[0] - p1[0]) ** 2 + (p0[1] - p1[1]) ** 2)
      const d2 = Math.sqrt((p1[0] - p2[0]) ** 2 + (p1[1] - p2[1]) ** 2)
      const d3 = Math.sqrt((p2[0] - p3[0]) ** 2 + (p2[1] - p3[1]) ** 2)

      // Bezier control point can be calulated as follows
      // B_0 = P_1
      // B_1 = [ -d_2^2𝛂⋅P_0 + (2⋅d_1^2𝛂 + 3⋅d_1^𝛂⋅d_2^𝛂 + d_2^2^𝛂)⋅P_1  + d_1^2𝛂⋅P_2 ] / [ 3⋅d_1^𝛂⋅(d_1^𝛂 + d_2^𝛂) ]
      // B_2 = [ -d_2^2𝛂⋅P_3 + (2⋅d_3^2𝛂 + 3⋅d_3^𝛂⋅d_2^𝛂 + d_2^2^𝛂)⋅P_1  + d_3^2𝛂⋅P_1 ] / [ 3⋅d_3^𝛂⋅(d_3^𝛂 + d_2^𝛂) ]
      // B_3 = P_2
      //
      //
      // Which can be writen as conversion matrix
      // A = 2⋅d1^2𝛂 + 3⋅d1^𝛂⋅d2^𝛂 + d3^2𝛂
      // B = 2⋅d3^2𝛂 + 3⋅d3^𝛂⋅d2^𝛂 + d2^2𝛂

      //                          [   0             1            0          0          ]   [ P_0 ]
      // [ B_0, B_1, B_2, B_3 ] = [   -d2^2𝛂 /N     A/N          d1^2𝛂 /N   0          ] ⋅ [ P_1 ]
      //                          [   0             d3^2𝛂 /M     B/M        -d2^2𝛂 /M  ]   [ P_2 ]
      //                          [   0             0            1          0          ]   [ P_3 ]

      // Apply parametrization
      const d3powA = Math.pow(d3, alpha)
      const d3pow2A = Math.pow(d3, alpha2)
      const d2powA = Math.pow(d2, alpha)
      const d2pow2A = Math.pow(d2, alpha2)
      const d1powA = Math.pow(d1, alpha)
      const d1pow2A = Math.pow(d1, alpha2)

      const A = 2 * d1pow2A + 3 * d1powA * d2powA + d2pow2A
      const B = 2 * d3pow2A + 3 * d3powA * d2powA + d2pow2A

      let N = 3 * d1powA * (d1powA + d2powA)
      if (N > 0) {
        N = 1 / N
      }

      let M = 3 * d3powA * (d3powA + d2powA)
      if (M > 0) {
        M = 1 / M
      }

      let bp1: Vec = [
        (-d2pow2A * p0[0] + A * p1[0] + d1pow2A * p2[0]) * N,
        (-d2pow2A * p0[1] + A * p1[1] + d1pow2A * p2[1]) * N,
      ]

      let bp2: Vec = [
        (d3pow2A * p1[0] + B * p2[0] - d2pow2A * p3[0]) * M,
        (d3pow2A * p1[1] + B * p2[1] - d2pow2A * p3[1]) * M,
      ]

      if (bp1[0] === 0 && bp1[1] === 0) {
        bp1 = p1
      }

      if (bp2[0] === 0 && bp2[1] === 0) {
        bp2 = p2
      }

      // result.push([{ ...lastStartPoint }, bp1, bp2, { ...p2 }])
      // result.moveTo(p2[0], p2[1])
      // result.lineTo(p2[0] + 1, p2[1] + 1)
      // result.arc(p2[0], p2[1], 0.1, 0, Math.PI * 2)
      // result.arcTo(bp1[0], bp1[1], p2[0], p2[1], 0)
      result.bezierCurveTo(bp1[0], bp1[1], bp2[0], bp2[1], p2[0], p2[1])

      lastStartPoint = p2
    }

    return result
  }
}
